import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import type { EChartsOption } from 'echarts';
import { NgxEchartsDirective } from 'ngx-echarts';
import { isString } from 'lodash';
import { buildEchartsTheme } from '../echarts-theme';

export interface IEchartsTreemapDatum {
  name: string;
  value: number;
  children?: IEchartsTreemapDatum[];
}

@Component({
  selector: 'pr-echarts-treemap',
  templateUrl: './echarts-treemap.component.html',
  styleUrls: ['./echarts-treemap.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
})
export class EchartsTreemapComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly data = input<IEchartsTreemapDatum[]>([]);
  readonly label = input.required<string>();
  readonly height = input<number>(280);
  readonly ariaLabelOverride = input<string>();

  readonly hasData = computed(() => this.data().length > 0);

  readonly ariaLabel = computed(() => {
    const override = this.ariaLabelOverride();
    if (override) {
      return override;
    }
    const items = this.data();
    if (items.length === 0) {
      return this.label();
    }
    const total = items.reduce((sum, item) => sum + item.value, 0);
    return `${this.label()} treemap chart, total ${total}, ${
      items.length
    } items`;
  });

  readonly chartOptions: Signal<EChartsOption> = computed(() => {
    const items = this.data();
    if (items.length === 0) {
      return {};
    }

    const theme = buildEchartsTheme(this._elementRef.nativeElement);
    const palette = (Array.isArray(theme.color) ? theme.color : []).filter(
      isString
    );

    return {
      ...theme,
      tooltip: {
        ...theme.tooltip,
        trigger: 'item',
      },
      series: [
        {
          type: 'treemap',
          name: this.label(),
          data: items,
          roam: false,
          breadcrumb: { show: false },
          upperLabel: { show: false },
          label: {
            show: true,
            formatter: '{b}\n{c}',
            fontSize: 11,
          },
          levels: [
            {
              color: palette,
              colorMappingBy: 'index',
              itemStyle: {
                borderColor: '#fff',
                borderWidth: 2,
                gapWidth: 2,
              },
            },
          ],
        },
      ],
    };
  });
}
